<template>
  <div class="border-input focus-within:border-ring focus-within:ring-ring/50 rounded-md border bg-transparent shadow-xs focus-within:ring-[3px] overflow-hidden">
    <div v-if="editor" class="flex items-center gap-1 flex-wrap border-b border-border bg-muted/40 px-2 py-1">
      <Toggle
        size="sm"
        :pressed="editor.isActive('bold')"
        @click="editor.chain().focus().toggleBold().run()"
        aria-label="Bold"
      >
        <BoldIcon class="size-4" />
      </Toggle>
      <Toggle
        size="sm"
        :pressed="editor.isActive('italic')"
        @click="editor.chain().focus().toggleItalic().run()"
        aria-label="Italic"
      >
        <ItalicIcon class="size-4" />
      </Toggle>
      <Separator orientation="vertical" class="h-6" />
      <Toggle
        size="sm"
        :pressed="editor.isActive('bulletList')"
        @click="editor.chain().focus().toggleBulletList().run()"
        aria-label="Bullet list"
      >
        <ListIcon class="size-4" />
      </Toggle>
      <Toggle
        size="sm"
        :pressed="editor.isActive('orderedList')"
        @click="editor.chain().focus().toggleOrderedList().run()"
        aria-label="Ordered list"
      >
        <ListOrderedIcon class="size-4" />
      </Toggle>
      <Separator orientation="vertical" class="h-6" />
      <Toggle
        size="sm"
        :pressed="editor.isActive('heading', { level: 2 })"
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
        aria-label="Heading 2"
      >
        <Heading2Icon class="size-4" />
      </Toggle>
      <Toggle
        size="sm"
        :pressed="editor.isActive('heading', { level: 3 })"
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
        aria-label="Heading 3"
      >
        <Heading3Icon class="size-4" />
      </Toggle>
      <Separator orientation="vertical" class="h-6" />
      <Toggle
        size="sm"
        :pressed="editor.isActive('blockquote')"
        @click="editor.chain().focus().toggleBlockquote().run()"
        aria-label="Blockquote"
      >
        <QuoteIcon class="size-4" />
      </Toggle>
      <Toggle
        size="sm"
        :pressed="editor.isActive('link')"
        @click="setLink"
        aria-label="Link"
      >
        <LinkIcon class="size-4" />
      </Toggle>
      <Toggle
        v-if="editor.isActive('link')"
        size="sm"
        @click="editor.chain().focus().unsetLink().run()"
        aria-label="Unlink"
      >
        <UnlinkIcon class="size-4" />
      </Toggle>
    </div>
    <EditorContent :editor="editor" class="tiptap-editor-content" data-test-id="tiptap-editor-content" />
  </div>
</template>

<script setup lang="ts">
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import {
  BoldIcon,
  Heading2Icon,
  Heading3Icon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  QuoteIcon,
  UnlinkIcon,
} from 'lucide-vue-next'
import { watch, nextTick } from 'vue'
import { Separator } from '@/components/ui/separator'
import { Toggle } from '@/components/ui/toggle'

const props = defineProps<{
  modelValue: string
  placeholder?: string
  id?: string
  ariaLabel?: string
  ariaLabelledby?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const editor = useEditor({
  extensions: [
    StarterKit,
    Placeholder.configure({
      placeholder: props.placeholder || 'Écrivez votre description...',
    }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    }),
  ],
  content: props.modelValue || '',
  editorProps: {
    attributes: {
      class: 'outline-none min-h-32 max-h-64 overflow-y-auto px-3 py-2 text-sm leading-relaxed',
      role: 'textbox',
      'aria-multiline': 'true',
    },
  },
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  },
})

watch(
  () => props.modelValue,
  (newValue) => {
    const isSame = editor.value?.getHTML() === newValue
    if (!isSame && newValue !== undefined) {
      editor.value?.commands.setContent(newValue, { emitUpdate: false })
    }
  },
)

watch(
  () => [editor.value, props.id, props.ariaLabel, props.ariaLabelledby] as const,
  async () => {
    await nextTick()
    const el = editor.value?.view.dom
    if (!el) return
    if (props.id) el.id = props.id
    else el.removeAttribute('id')
    if (props.ariaLabel) el.setAttribute('aria-label', props.ariaLabel)
    else el.removeAttribute('aria-label')
    if (props.ariaLabelledby) el.setAttribute('aria-labelledby', props.ariaLabelledby)
    else el.removeAttribute('aria-labelledby')
  },
  { immediate: true },
)

function setLink() {
  if (!editor.value) return
  const previousUrl = editor.value.getAttributes('link').href
  const url = window.prompt('URL du lien:', previousUrl)
  if (url === null) return
  if (url === '') {
    editor.value.chain().focus().unsetLink().run()
    return
  }
  editor.value.chain().focus().setLink({ href: url }).run()
}
</script>

<style>
.tiptap-editor-content .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: var(--muted-foreground);
  pointer-events: none;
  height: 0;
}

.tiptap-editor-content .ProseMirror h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.tiptap-editor-content .ProseMirror h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 0.75rem;
  margin-bottom: 0.375rem;
}

.tiptap-editor-content .ProseMirror p {
  margin-bottom: 0.5rem;
}

.tiptap-editor-content .ProseMirror ul {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
  list-style-type: disc;
}

.tiptap-editor-content .ProseMirror ol {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
  list-style-type: decimal;
}

.tiptap-editor-content .ProseMirror li {
  margin-bottom: 0.25rem;
  display: list-item;
}

.tiptap-editor-content .ProseMirror blockquote {
  border-left: 3px solid var(--border);
  padding-left: 0.75rem;
  margin-bottom: 0.5rem;
  font-style: italic;
  color: var(--muted-foreground);
}

.tiptap-editor-content .ProseMirror a {
  color: var(--primary);
  text-decoration: underline;
}

.tiptap-editor-content .ProseMirror code {
  background-color: var(--muted);
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-family: monospace;
  font-size: 0.875rem;
}
</style>
